// src/pages/ItemsPage.jsx

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner";
import { fetchItems } from "../api";
import { imgurSize, imgurSrcSet } from "../utils/imgur";
import "../App.css";

function ItemsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await fetchItems();
        if (!cancelled) setItems(data);
      } catch (err) {
        console.error("❌ Error fetching items:", err);
        if (!cancelled) {
          // ApiError carries `status`; 0 means the request never left the
          // device, which is a different problem from a server error.
          setError(
            err.status === 0
              ? "Could not reach the gallery. Check your connection and try again."
              : "The artworks couldn't be loaded just now. Please refresh in a moment."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="auction-container">
        <p className="error-text" role="alert">{error}</p>
        <button
          type="button"
          className="cta-button"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="auction-container gallery-page">
      <h1>The Living Tapestry Yarn</h1>
      <p className="gallery-intro">
        Browse through the Collection of Threads and Yarn.
      </p>

      {items.length === 0 ? (
        <p className="admin-empty">No artworks are listed yet — please check back soon.</p>
      ) : (
        <div className="art-grid">
          {items.map((item, index) => (
            /* Exactly seven children, in this order — see the note in
               HomePage.jsx. The grid uses grid-template-rows: subgrid, so a
               missing or reordered child shifts everything after it up a row
               and the cards stop aligning. */
            <div className="art-card" key={item.id}>
              {/* 1 — image.
                  No className="item-image" here: that class belongs to the
                  item page (max-width 600px, natural ratio) and fought this
                  card's 4:3 rule, so images sized differently from the
                  identical cards on the homepage. `.art-card img` handles it.

                  srcSet requests the 320px or 640px Imgur variant rather than
                  the ~4MB original. */}
              <img
                src={imgurSize(item.images?.[0], "m")}
                srcSet={imgurSrcSet(item.images?.[0], ["m", "l"])}
                sizes="(max-width: 600px) 90vw, (max-width: 1000px) 45vw, 300px"
                alt={`${item.title} by ${item.artist}`}
                width={800}
                height={600}
                loading={index < 3 ? "eager" : "lazy"}
                decoding="async"
              />

              {/* 2 — title */}
              <h3>{item.title}</h3>

              {/* 3 — artist */}
              <p><em>by {item.artist}</em></p>

              {/* 4 — description */}
              <p className="short-desc">{item.short_description}</p>

              {/* 5 — tags */}
              <div className="jewel-tags">
                {item.jewels.map((jewel, idx) => (
                  <span className="jewel-badge" key={idx}>{jewel}</span>
                ))}
              </div>

              {/* 6 — badge slot; rendered even when empty to hold the row */}
              <div className="badge-slot">
                {!item.for_sale && (
                  <span className="exhibition-badge">Exhibition only</span>
                )}
              </div>

              {/* 7 — CTA */}
              <Link
                to={`/auction/item/${item.id}`}
                className="cta-button"
                aria-label={`View ${item.title}`}
              >
                View
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ItemsPage;